import uniqBy from 'lodash.uniqby'
import {
  createFieldValue,
  getMostRecentFieldValue,
  getNestedElementById,
  getNestedOrFloatingElementById,
  getNextUuid,
  getPersonName,
  getRelationshipLabel,
  getRelationshipToPrimaryPersonText,
  getSuperOfElementForPerson,
} from '@fs/zion-sls'
import { FamilyReviewApp } from '../../../../shared/services/config'

/**
 * Get the first primary person and default to the first person if there is no primary.
 * @param {*} currentTask the family review task (ie the record element)
 * @returns person element
 */
export const getPrimaryOrFirstPerson = (currentTask) => {
  const primary = currentTask?.subElements?.find((el) => el.primary)
  const first = currentTask?.subElements?.filter((el) => el.elementType === 'PERSON')[0]
  return primary || first
}

/**
 * Get the name of the primary person
 * @param {*} currentTask the family review task (ie the record element)
 * @returns displayable string of the person name
 */
export const getPrimaryName = (currentTask) => {
  return getPersonName({ person: getPrimaryOrFirstPerson(currentTask), justOneName: true })
}

/**
 * Get the field values to display in cards
 * @param {*} placeElements
 * @returns
 */
export const getFieldValues = (elements) => {
  return elements
    ?.flatMap(
      (element) =>
        element.subElements?.flatMap((subElement) => ({
          displayLabel: subElement?.displayLabel,
          value: getMostRecentFieldValue(subElement),
          id: subElement?.id,
          fieldType: subElement?.fieldType,
          superElements: subElement?.superElements,
          personId: subElement?.personId,
        })) || []
    )
    .filter((field) => field.value)
}

export const getEventFieldIds = (event) => {
  const placeFields = event?.subElements?.filter((se) => se?.elementType === 'PLACE') || []
  const dateFields = event?.subElements?.filter((se) => se?.elementType === 'DATE') || []
  const typeFields = event?.subElements?.filter((se) => se?.fieldType === 'EVENT_TYPE') || []
  return placeFields
    .concat(dateFields)
    .concat(typeFields)
    .flatMap((field) => field?.subElements)
    .flatMap((se) => se?.id)
}

export const getRelationshipText = (person, types, record, t) => {
  const relationshipToPrimaryValue = getRelationshipToPrimaryPersonText(person, types, record, t)
  return (
    relationshipToPrimaryValue ||
    t('family-review.step2.relationship-label.no-direction-relation', 'No Direct Relation')
  )
}

/**
 * Retrieve the most recent field value from a place field
 * @param {Object} event event element type object from sls
 * @returns field value array like [{"displayLabel": "Place", "id": "c0374662-2347-439c-b776-869223e8dcef", "value": "Pedregal, Falcón, Venezuela"}]
 */
export const getPlaceFieldValues = (event) =>
  getFieldValues(event?.subElements?.filter((el) => el.elementType === 'PLACE'))

/**
 * Retrieve the most recent field value from a date field
 * @param {Object} event event element type object from sls
 * @returns field value string
 */
export const getDateFieldValues = (event) =>
  getFieldValues(event?.subElements?.filter((el) => el.elementType === 'DATE'))

/**
 * Retrieve all the people in a given record
 * @param {Object} recordElement in sls or the family review currentTask
 * @returns {Array} list of persons sorted by primary first
 */
export const getAllPersons = (recordElement) => {
  // put the primary person first
  const sortPersons = (a, b) => {
    if (!a.primary && b.primary) return 1
    if (a.primary && !b.primary) return -1
    return 0
  }
  return recordElement?.subElements?.filter((e) => e.elementType === 'PERSON').sort(sortPersons)
}
/**
 * Retrieve all the events in a given record
 * @param {*} recordElement the sls record
 * @returns {Array} of event sls elements
 */
export const getAllEvents = (recordElement) => {
  const dedupedEvents = []
  const allEvents = recordElement?.subElements
    ?.filter((se) => se.elementType === 'PERSON')
    .flatMap((person) => person?.subElements?.filter((el) => el.elementType === 'EVENT'))
  // dedupe events
  allEvents.forEach((event) => {
    if (!dedupedEvents.includes(event.id)) dedupedEvents.push(event)
  })
  return dedupedEvents
}
/**
 * Retrieve all the possible event types on this record
 * @param {Array} types event types in the zionSLSState
 * @returns {Function} translation function
 */
export const getEventTypeSuggestions = ({ types, t, isPrimary = false, primaryEventType }) => {
  const filterOutPrimaryTypes = (type) => !type.primaryOnly
  const results = uniqBy(
    types?.events
      ?.filter((type) => (isPrimary ? true : filterOutPrimaryTypes(type))) // if it is a primary only event, we want to include all types
      ?.filter((type) => type.eventType !== primaryEventType) // if this event exists as a primary event, filter it out so there are no dupes of primary events
      .map((type) => ({
        key: type.eventType,
        primaryText: type.displayLabel,
        group: t('add-person.record-types', 'Record types'),
      })),
    'key'
  ) || [{ key: 'notImplementd', primaryText: t('add-person.types-not-implemented', 'Types Not Implemented') }]
  return results
}

/**
 * Returns a list of rel types like 'Husband', using the appropriate name for the sexValue passe din
 * @param {Array} types - zionSlsState.types
 * @param {String} sexValue - field resource for the person the relationship is for
 * @returns {*}
 */
export const getGenderedRelTypeSuggestions = (types, sexValue) => {
  return types?.relationships?.reduce((acc, rel) => {
    const otherPerson = {
      subElements: [
        {
          fieldType: 'SEX_CODE',
          fieldValues: [
            {
              normalizedValues: [
                {
                  resource: `http://gedcomx.org/${sexValue}`,
                },
              ],
            },
          ],
        },
      ],
    }
    let firstRelationshipLabel = ''
    let secondRelationshipLabel = ''
    firstRelationshipLabel = getRelationshipLabel({ otherPerson, order: 'FIRST', typeData: rel })
    secondRelationshipLabel = getRelationshipLabel({ otherPerson, order: 'SECOND', typeData: rel })

    acc.push({
      key: `${rel.relType}_FIRST`,
      primaryText: firstRelationshipLabel,
    })
    if (firstRelationshipLabel !== secondRelationshipLabel) {
      acc.push({
        key: `${rel.relType}_SECOND`,
        primaryText: secondRelationshipLabel,
      })
    }
    return acc
  }, [])
}

/**
 * Get relType and order from a gendered rel type suggestion
 * @param {String} value - the option key
 * @returns {{relType: *, order: *}}
 */
export const getRelTypeAndOrder = (value) => {
  if (!value || !value.key) {
    return { relType: '', order: '' }
  }
  const strParts = value.key.split('_')
  const order = strParts[strParts.length - 1]
  strParts.pop()
  const relType = strParts.join('_')
  return { relType, order }
}

/**
 * Determines the correct level to remove a subelement to make room for a shared field to be added
 * @param {Object} currentTask The current task object, an SLS record
 * @param {Object} person An SLS person object
 * @param {String} fieldId The id of the field that is being removed
 * @returns {Object} An object containing the dispatch to remove the subelement, the subelement to being removed, and the super of that subelement
 */
const createRemSubToPrepForSharedField = (currentTask, person, fieldId) => {
  let subToRemove = getNestedOrFloatingElementById([currentTask], fieldId)
  let superOfSubToRemove = null
  // If the subelement can only have one super, then we just need to remove its only super from the next super up
  if (subToRemove.typeData.singleSuper) {
    subToRemove = getNestedOrFloatingElementById([currentTask], subToRemove.superElements[0].id)
    superOfSubToRemove = getSuperOfElementForPerson(person, subToRemove)
  } else {
    // If the subelement has only one super element, then we can assume that the super we need is the first in the array
    // Otherwise, we need to make sure the super we use is the one associated with this person
    if (subToRemove.superElements.length === 1) {
      superOfSubToRemove = getNestedOrFloatingElementById([currentTask], subToRemove.superElements[0].id)
    } else {
      superOfSubToRemove = getSuperOfElementForPerson(person, subToRemove)
    }
    // If the super we have is shared, then we need to go up to its super to REM_SUB so that we only remove it from this person
    if (superOfSubToRemove.superElements.length > 1) {
      subToRemove = superOfSubToRemove
      superOfSubToRemove = getSuperOfElementForPerson(person, subToRemove)
    }
  }

  const removeSubDispatch = {
    type: 'REM_SUB',
    value: {
      superId: superOfSubToRemove.id,
      superPartitionKey: superOfSubToRemove.partitionKey,
      subId: subToRemove.id,
      subPartitionKey: subToRemove.partitionKey,
      elementType: superOfSubToRemove.elementType,
    },
  }

  return { removeSubDispatch, subToRemove, superOfSubToRemove }
}

/**
 * Dynamically creates the dispatches needed to:
 * 1. Remove the old field from the person
 * 2. Add the shared field to the person
 * 3. Update the value of the shared field with the value that was in the form
 * @param {Object} currentTask The current task object, an SLS record object
 * @param {Object} person An SLS person object, the person that the shared field is being added to
 * @param {String} fieldId The id of the field that is being removed
 * @param {String} fieldValue The form value that is being added to the shared field
 * @param {Object} sharedField The SLS element or field object that is being added to the person
 * @returns {Array<Object>} An array of dispatch objects to be dispatched to SLS
 */
export const handleUpdateSharedField = (currentTask, person, fieldId, fieldValue, sharedField) => {
  const dispatches = []
  const { removeSubDispatch, subToRemove, superOfSubToRemove } = createRemSubToPrepForSharedField(
    currentTask,
    person,
    fieldId
  )
  dispatches.push(removeSubDispatch)

  if (sharedField.typeData.singleSuper) {
    sharedField = getNestedOrFloatingElementById([currentTask], sharedField.superElements[0].id)
  }

  const superOfSharedField = getNestedOrFloatingElementById([currentTask], sharedField.superElements[0].id)

  if (superOfSharedField.elementType === subToRemove.elementType) {
    // Dispatches to CREATE_ELEMENT to replace removed super and ADD_SUB it to its former super
    const newElementId = getNextUuid()
    const createSuperElementDispatch = {
      type: 'CREATE_ELEMENT',
      value: {
        partitionKey: superOfSubToRemove.partitionKey,
        elementType: subToRemove.elementType,
        id: newElementId,
      },
    }
    const addSuperElementDispatch = {
      type: 'ADD_SUB',
      value: {
        superId: superOfSubToRemove.id,
        superPartitionKey: superOfSubToRemove.partitionKey,
        subId: newElementId,
        subPartitionKey: superOfSubToRemove.partitionKey,
        elementType: superOfSubToRemove.elementType,
      },
    }
    const addSharedFieldDispatch = {
      type: 'ADD_SUB',
      value: {
        superId: newElementId,
        superPartitionKey: superOfSubToRemove.partitionKey,
        subId: sharedField.id,
        subPartitionKey: sharedField.partitionKey,
        elementType: subToRemove.elementType,
      },
    }
    dispatches.push(createSuperElementDispatch, addSuperElementDispatch, addSharedFieldDispatch)
  } else {
    const addSharedFieldDispatch = {
      type: 'ADD_SUB',
      value: {
        superId: superOfSubToRemove.id,
        superPartitionKey: superOfSubToRemove.partitionKey,
        subId: sharedField.id,
        subPartitionKey: sharedField.partitionKey,
        elementType: superOfSubToRemove.elementType,
      },
    }
    dispatches.push(addSharedFieldDispatch)
  }

  const fieldValueArg = createFieldValue(fieldValue, { comment: '', reasonId: '', app: FamilyReviewApp })
  const sharedFieldValueDispatch = {
    type: 'ADD_FV',
    value: {
      fieldValue: fieldValueArg,
      id: sharedField.id,
      partitionKey: sharedField.partitionKey,
    },
  }
  dispatches.push(sharedFieldValueDispatch)

  return dispatches
}

/**
 * Checks if there are persons associated with different partition keys (indicating different images) in the current task.
 * @param {Object} currentTask - The current family review task (i.e., the record element).
 * @returns {Boolean} - Returns true if there are persons on two different images, false otherwise.
 */
export const hasPersonsOnTwoImages = (currentTask) => {
  let hasPersonOnTwoImages = false
  if (currentTask && currentTask?.subElements) {
    currentTask.subElements.forEach((person) => {
      if (person.partitionKey !== currentTask.partitionKey) {
        hasPersonOnTwoImages = true
      }
    })
  }
  return hasPersonOnTwoImages
}

/**
 * Custom sorting function for suggestions based on user input.
 *
 * @param {string} inputText - The text input by the user.
 * @param {Object} a - The first suggestion object to compare.
 * @param {Object} b - The second suggestion object to compare.
 * @param {string} a.primaryText - The primary text of the first suggestion.
 * @param {string} b.primaryText - The primary text of the second suggestion.
 * @returns {number} - Returns -1 if the first suggestion should come before the second,
 *                     1 if the first suggestion should come after the second,
 *                     or 0 if they are considered equal.
 *
 * The function prioritizes suggestions that start with the input text.
 * If both suggestions start with the input text, they are further sorted by length.
 * If the input text is empty, the original order of suggestions is maintained.
 */
export const sortFunction = (inputText, a, b) => {
  const normalizedInputText = inputText?.toLowerCase().trim()
  const aText = a.primaryText.toLowerCase()
  const bText = b.primaryText.toLowerCase()

  const aStartsWithInput = aText.startsWith(normalizedInputText)
  const bStartsWithInput = bText.startsWith(normalizedInputText)

  // If inputText is empty, maintain the original order
  if (!normalizedInputText) {
    return 0
  }

  if (aStartsWithInput && !bStartsWithInput) return -1
  if (!aStartsWithInput && bStartsWithInput) return 1

  if (aStartsWithInput && bStartsWithInput) {
    // Both start with the input text, so compare by length
    return aText.length - bText.length
  }

  // Neither start with the input text, maintain the original order
  return 0
}

/**
 * Gets the arks of the persons on images different from the current task. (FamilyReview specific)
 * @param {Object} currentTask - The current family review task (i.e., the record element).
 * @returns {Array} - An array of arks of persons on different images.
 */
export const getImageArksOfPeopleNotOnImage = (currentTask) => {
  const arks = []
  if (currentTask && currentTask?.subElements) {
    currentTask.subElements.forEach((person) => {
      if (person.partitionKey !== currentTask.partitionKey) {
        arks.push(person.partitionKey)
      }
    })
  }
  return arks
}

/**
 * Get the background color based on review and focus status.
 *
 * @param {boolean} hasBeenReviewed - Whether the item has been reviewed.
 * @param {boolean} isFocused - Whether the item is currently focused.
 * @param {string} defaultColor - The default color if not reviewed or focused.
 * @param {string} focusedColor - The color if the item is focused.
 * @param {string} reviewedColor - The color if the item has been reviewed.
 * @returns {string} - The appropriate background color.
 */
export const getBackgroundColor = (hasBeenReviewed, isFocused, defaultColor, focusedColor, reviewedColor) => {
  if (hasBeenReviewed) {
    return reviewedColor
  }
  if (isFocused) {
    return focusedColor
  }
  return defaultColor
}

/**
 * Get the name of the person associated with the event.
 * @param {Object} event - The event object.
 * @param {Object} currentTask - The current family review task (i.e., the record element).
 * @returns {string} - The name of the person associated with the event.
 */
export const getEventPersonName = (event, currentTask) => {
  const eventPersonId = event?.personId || event?.superElements?.find((element) => element.id.includes('1:1'))?.id
  const eventPerson = getNestedElementById(currentTask?.subElements, eventPersonId)
  return getPersonName({ person: eventPerson, justOneName: true })
}

/**
 * Groups events by person and returns them as a flat array in the order they are visually displayed, with primary events first.
 * Each primary event will be included only once.
 * @param {Array} events - Array of event objects to be grouped and sorted.
 * @param {Object} currentTask - The current family review task (i.e., the record element).
 * @returns {Array} - Array of all event objects in the correct order.
 */
export const groupEventsByPerson = (events, currentTask) => {
  const groupedEvents = {}

  // Get the primary person
  const primaryPerson = getPrimaryOrFirstPerson(currentTask)
  const primaryPersonId = primaryPerson?.id

  // Group events by person using personId as the key
  events.forEach((event) => {
    const personId = event?.personId

    if (!groupedEvents[personId]) {
      groupedEvents[personId] = { primaryEvents: null, nonPrimaryEvents: [] }
    }

    if (event.primary) {
      if (personId === primaryPersonId) {
        groupedEvents[personId].primaryEvents = event
      }
    } else {
      groupedEvents[personId].nonPrimaryEvents.push(event)
    }
  })

  // Alphabetize non-primary events for each person
  Object.keys(groupedEvents).forEach((personId) => {
    groupedEvents[personId].nonPrimaryEvents.sort((a, b) => a.label.localeCompare(b.label))
  })

  // Flatten the grouped events into a single array while preserving order
  const orderedEvents = []

  // Add the primary event of the primary person first
  if (primaryPersonId && groupedEvents[primaryPersonId]?.primaryEvents) {
    orderedEvents.push(groupedEvents[primaryPersonId].primaryEvents)
  }

  // Add non-primary events of the primary person
  if (primaryPersonId && groupedEvents[primaryPersonId]) {
    orderedEvents.push(...groupedEvents[primaryPersonId].nonPrimaryEvents)
    delete groupedEvents[primaryPersonId]
  }

  // Add non-primary events of other persons, excluding primary events
  Object.keys(groupedEvents).forEach((personId) => {
    orderedEvents.push(...groupedEvents[personId].nonPrimaryEvents)
  })

  return orderedEvents
}
